import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Logo from './Logo';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const serviceLinks = [
    {
      to: '/bryllupper',
      label: 'Bryllupper',
      desc: 'Stemningsfuld bryllupsfoto & video samt bryllupspakker',
      icon: 'fa-light fa-rings-wedding',
    },
    {
      to: '/cykelloeb',
      label: 'Cykelløb',
      desc: 'Fart, intensitet og feltets puls på landevejen',
      icon: 'fa-light fa-person-biking',
    },
    {
      to: '/events',
      label: 'Events & Erhverv',
      desc: 'Levende dokumentation til markedsføring og SoMe',
      icon: 'fa-light fa-camera-movie',
    },
    {
      to: '/familiefotografering',
      label: 'Familiefotografering',
      desc: 'Nærværende stunder hjemme eller ude i naturen',
      icon: 'fa-light fa-people-roof',
    },
  ];

  const isServicesActive = serviceLinks.some(
    (link) => location.pathname === link.to
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-stone-200 ${
        scrolled
          ? 'py-3.5 shadow-sm'
          : 'py-4.5 sm:py-5 shadow-xs'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between">
        {/* Brand Logo */}
        <Link
          to="/"
          className="group flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
          aria-label="Lunde Media forside"
        >
          <Logo variant="dark" />
        </Link>

        {/* Desktop Navigation (strictly >= 1200px) */}
        <nav
          className="hidden min-[1200px]:flex items-center gap-8"
          aria-label="Hovednavigation"
        >
          {/* Forside */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `text-base font-medium tracking-wide transition-colors duration-200 relative py-1 ${
                isActive
                  ? 'text-stone-950 font-semibold'
                  : 'text-stone-600 hover:text-stone-950'
              }`
            }
          >
            {({ isActive }) => (
              <>
                Forside
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#141416] rounded-none" />
                )}
              </>
            )}
          </NavLink>

          {/* Ydelser Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
              onMouseEnter={() => setServicesDropdownOpen(true)}
              className={`text-base font-medium tracking-wide transition-colors duration-200 flex items-center gap-1.5 py-1 ${
                isServicesActive
                  ? 'text-stone-950 font-semibold'
                  : 'text-stone-600 hover:text-stone-950'
              }`}
              aria-expanded={servicesDropdownOpen}
              aria-haspopup="true"
            >
              <span>Ydelser</span>
              <i
                className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                  servicesDropdownOpen ? 'rotate-180 text-black' : 'opacity-60 text-stone-500'
                }`}
                aria-hidden="true"
              />
              {isServicesActive && (
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#141416] rounded-none" />
              )}
            </button>

            {/* Dropdown Menu */}
            {servicesDropdownOpen && (
              <div
                onMouseLeave={() => setServicesDropdownOpen(false)}
                className="absolute top-full left-0 mt-2 w-72 bg-white border border-stone-200 py-2 shadow-xl backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-150"
              >
                <div>
                  {serviceLinks.map((item) => {
                    const active = location.pathname === item.to;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`block px-4 py-2.5 text-base transition-colors ${
                          active
                            ? 'text-black font-semibold bg-stone-100'
                            : 'text-stone-700 hover:text-stone-950 hover:bg-stone-50'
                        }`}
                      >
                        <div className="font-medium leading-snug">
                          {item.label}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Branding */}
          <NavLink
            to="/branding"
            className={({ isActive }) =>
              `text-base font-medium tracking-wide transition-colors duration-200 relative py-1 ${
                isActive
                  ? 'text-stone-950 font-semibold'
                  : 'text-stone-600 hover:text-stone-950'
              }`
            }
          >
            {({ isActive }) => (
              <>
                Branding
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#141416] rounded-none" />
                )}
              </>
            )}
          </NavLink>

          <NavLink
            to="/storytelling"
            className={({ isActive }) =>
              `text-base font-medium tracking-wide transition-colors duration-200 relative py-1 ${
                isActive
                  ? 'text-stone-950 font-semibold'
                  : 'text-stone-600 hover:text-stone-950'
              }`
            }
          >
            {({ isActive }) => (
              <>
                Storytelling
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#141416] rounded-none" />
                )}
              </>
            )}
          </NavLink>

          <NavLink
            to="/om-lunde-media"
            className={({ isActive }) =>
              `text-base font-medium tracking-wide transition-colors duration-200 relative py-1 ${
                isActive
                  ? 'text-stone-950 font-semibold'
                  : 'text-stone-600 hover:text-stone-950'
              }`
            }
          >
            {({ isActive }) => (
              <>
                Om Lunde Media
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#141416] rounded-none" />
                )}
              </>
            )}
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `text-base font-medium tracking-wide transition-colors duration-200 relative py-1 ${
                isActive
                  ? 'text-stone-950 font-semibold'
                  : 'text-stone-600 hover:text-stone-950'
              }`
            }
          >
            {({ isActive }) => (
              <>
                Kontakt
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#141416] rounded-none" />
                )}
              </>
            )}
          </NavLink>
        </nav>

        {/* Desktop CTA Button (strictly >= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center gap-5">
          <a
            href="tel:26368306"
            className="text-sm md:text-base text-stone-800 hover:text-black transition-colors whitespace-nowrap flex items-center gap-2 font-semibold tracking-wide"
            title="Ring til Michael"
          >
            <i className="fa-solid fa-phone text-black text-xs" aria-hidden="true" />
            <span className="whitespace-nowrap font-bold">26 36 83 06</span>
          </a>
          <Link
            to="/kontakt"
            className="cta-btn inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#141416] hover:bg-black text-white font-medium text-sm transition-all shadow-xs"
          >
            Book tid / Kontakt
          </Link>
        </div>

        {/* Mobile Burger Button (strictly < 1200px) */}
        <div className="flex min-[1200px]:hidden items-center gap-1">
          <a
            href="tel:26368306"
            className="p-2 text-stone-900 transition-colors"
            aria-label="Ring til Michael på 26 36 83 06"
          >
            <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-stone-900 transition-colors focus:outline-none"
            aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
            aria-expanded={mobileMenuOpen}
          >
            <i
              className={`fa-solid ${
                mobileMenuOpen ? 'fa-xmark' : 'fa-bars'
              } text-xl`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Sidebar / Drawer (strictly < 1200px) */}
      <div
        className={`min-[1200px]:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop overlay */}
        <div
          onClick={() => setMobileMenuOpen(false)}
          className="absolute inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
          aria-hidden="true"
        />

        {/* Sliding Sidebar */}
        <aside
          className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl flex flex-col justify-between overflow-y-auto transform transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          aria-label="Mobil navigation"
        >
          {/* Sidebar Header with Logo and Close button */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center"
            >
              <Logo
                inverted={false}
                subtitle="Fotograf & Videograf"
                size="md"
              />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 text-stone-800 transition-colors focus:outline-none"
              aria-label="Luk menu"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Sidebar Body */}
          <div className="px-6 py-6 flex-1 flex flex-col justify-between">
            <div className="space-y-6">
              {/* Navigation links */}
              <div className="space-y-1">
                <NavLink
                  to="/"
                  end
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3.5 px-3.5 text-lg transition-colors ${
                      isActive
                        ? 'bg-zinc-100 text-zinc-950 font-semibold'
                        : 'text-stone-800 hover:bg-stone-50 hover:text-stone-950 font-medium'
                    }`
                  }
                >
                  <span>Forside</span>
                  <i className="fa-solid fa-chevron-right text-xs opacity-60" aria-hidden="true" />
                </NavLink>

                {/* Ydelser Accordion */}
                <div className="border-y border-stone-100 py-2">
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="w-full flex items-center justify-between py-2.5 px-3.5 text-lg font-medium text-stone-900"
                  >
                    <span>Ydelser & Gallerier</span>
                    <i
                      className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                        mobileServicesOpen ? 'rotate-180 text-black' : 'opacity-40'
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  {mobileServicesOpen && (
                    <div className="mt-1 pl-3 space-y-1 border-l-2 border-stone-200 ml-3">
                      {serviceLinks.map((item) => {
                        const active = location.pathname === item.to;
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block py-2.5 px-3.5 text-base transition-colors ${
                              active
                                ? 'bg-zinc-100 text-zinc-950 font-semibold'
                                : 'text-stone-700 hover:text-stone-950 hover:bg-stone-50 font-medium'
                            }`}
                          >
                            <div>{item.label}</div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Mobil Branding Link */}
                <NavLink
                  to="/branding"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3.5 px-3.5 text-lg transition-colors ${
                      isActive
                        ? 'bg-zinc-100 text-zinc-950 font-semibold'
                        : 'text-stone-800 hover:bg-stone-50 hover:text-stone-950 font-medium'
                    }`
                  }
                >
                  <span>Branding</span>
                  <i className="fa-solid fa-chevron-right text-xs opacity-60" aria-hidden="true" />
                </NavLink>

                <NavLink
                  to="/storytelling"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3.5 px-3.5 text-lg transition-colors ${
                      isActive
                        ? 'bg-zinc-100 text-zinc-950 font-semibold'
                        : 'text-stone-800 hover:bg-stone-50 hover:text-stone-950 font-medium'
                    }`
                  }
                >
                  <span>Storytelling</span>
                  <i className="fa-solid fa-chevron-right text-xs opacity-60" aria-hidden="true" />
                </NavLink>

                <NavLink
                  to="/om-lunde-media"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3.5 px-3.5 text-lg transition-colors ${
                      isActive
                        ? 'bg-zinc-100 text-zinc-950 font-semibold'
                        : 'text-stone-800 hover:bg-stone-50 hover:text-stone-950 font-medium'
                    }`
                  }
                >
                  <span>Om Lunde Media</span>
                  <i className="fa-solid fa-chevron-right text-xs opacity-60" aria-hidden="true" />
                </NavLink>

                <NavLink
                  to="/kontakt"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3.5 px-3.5 text-lg transition-colors ${
                      isActive
                        ? 'bg-zinc-100 text-zinc-950 font-semibold'
                        : 'text-stone-800 hover:bg-stone-50 hover:text-stone-950 font-medium'
                    }`
                  }
                >
                  <span>Kontakt</span>
                  <i className="fa-solid fa-chevron-right text-xs opacity-60" aria-hidden="true" />
                </NavLink>
              </div>

              {/* Direct Booking CTA */}
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  onClick={() => setMobileMenuOpen(false)}
                  className="cta-btn w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#141416] hover:bg-black text-white font-semibold text-sm tracking-wide transition-all shadow-md"
                >
                  <span>Book tid / Kontakt Michael</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Bottom contact info snippet */}
            <div className="pt-6 border-t border-stone-200 mt-8 space-y-3">
              <div className="text-xs uppercase tracking-widest font-semibold text-stone-500">
                Direkte kontakt
              </div>
              <div className="flex flex-col gap-2 text-sm text-stone-600">
                <a
                  href="tel:26368306"
                  className="flex items-center gap-2.5 text-stone-800 hover:text-[#96642E] transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#96642E] text-xs" aria-hidden="true" />
                  <span className="whitespace-nowrap font-medium">26 36 83 06</span>
                </a>
                <a
                  href="mailto:Kontakt@lundemedia.dk"
                  className="flex items-center gap-2.5 text-stone-800 hover:text-[#96642E] transition-colors"
                >
                  <i className="fa-solid fa-envelope text-[#96642E] text-xs" aria-hidden="true" />
                  <span className="whitespace-nowrap font-medium">Kontakt@lundemedia.dk</span>
                </a>
                <div className="flex items-center gap-2.5 text-stone-500 text-xs">
                  <i className="fa-solid fa-location-dot text-[#96642E] text-xs" aria-hidden="true" />
                  <span>Holder til i Kolding</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </header>
  );
}
